import type { Dictionary } from '@ngrx/entity';
import type { Uuid } from '@common/types';
import type { NormalizedColumnEntity } from '@web/types';
import type { ColumnsState } from './columns.reducer';
import type { PageStructureState } from '../page-structure-feature.reducer';

import { createSelector } from '@ngrx/store';
import { denormalize } from 'normalizr';

import { selectGridsEntities } from '@web/store/page-structure/grids/grids.selectors';
import { selectWidgetsEntities } from '@web/store/page-structure/widgets/widgets.selectors';
import { columnSchema } from '@web/store/page-structure/page-structure/mockStructureData';

import { COLUMNS_FEATURE_KEY, columnsAdapter } from './columns.reducer';
import { pageStructureState } from '../page-structure-state.selectors';

const { selectAll, selectEntities, selectIds } = columnsAdapter.getSelectors();

// Lookup the 'Columns' feature state managed by NgRx
export const selectColumnsState = createSelector(
    pageStructureState,
    (state: PageStructureState): ColumnsState => state[COLUMNS_FEATURE_KEY]
);

export const selectAllColumns = createSelector(selectColumnsState, (state: ColumnsState) => selectAll(state));

export const selectColumnsEntities = createSelector(selectColumnsState, (state: ColumnsState) => selectEntities(state));

export const selectColumnsIds = createSelector(selectColumnsState, (state: ColumnsState) => selectIds(state));

export const selectColumnById = (id: Uuid) =>
    createSelector(selectColumnsEntities, (entities: Dictionary<NormalizedColumnEntity>) => entities[id]);

export const selectDenormalizedColumnById = (id: Uuid) =>
    createSelector(
        selectColumnsEntities,
        selectGridsEntities,
        selectWidgetsEntities,
        (columns, structures, widgets) => {
            const normalizedStructure = {
                columns,
                widgets: {
                    ...widgets,
                    ...structures,
                },
            };

            return denormalize(id, columnSchema, normalizedStructure);
        }
    );
